import React, { useState, useEffect, useCallback, useRef, act } from "react";
import {
  CSpinner,
  CTooltip,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CDropdownItem,
  CFormLabel,
  CTabContent,
  CTab,
  CTabs,
  CTabList,
  CTabPanel,
  CButton,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from "@coreui/react-pro";
import { useNavigate, useParams } from "react-router-dom";
import {
  uploadQuoteQuotation,
  getQuoteRequest,
  updateQuoteRequest,
  deleteQuoteQuotation,
  updateQuoteQuotation,
  revertStatus,
  saveDraft,
  findMotorPredefined,
  getMotorPredefined,
} from "../../utils/backend";
import { Formik, Form, Field, FieldArray } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "./uploadQuotation.css";
import CIcon from "@coreui/icons-react";
import { cilPen, cilSave, cilTrash, cilX } from "@coreui/icons";
import { formatDateTime } from "../../components/FormattedTime/FormatDateTime";

const UploadQuotation = () => {
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [order, setOrder] = useState({});
  const [insurance_name, setInsuranceName] = useState("");
  const [plan_name, setPlanName] = useState("");
  const id = useParams();
  const [initialValues, setInitialValues] = useState({
    insurance: "",
    plan_name: "",
    policy_wording: "",
    product_disclosure_sheet: "",
    premium_price: "",
    sum_insured: "",
    ncd_rate: "",
    add_ons: [{ add_on_name: "", add_on_price: "" }],
    plan_benefits: [""],
  });
  const [tabs, setTabs] = useState([
    { key: "tab1", label: "Tab 1", values: { ...initialValues } },
  ]);

  const [activeTab, setActiveTab] = useState(tabs[0]?.key);
  const formikRefs = useRef([]);

  const [isLocked, setIsLocked] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [planNameField, setPlanNameField] = useState([]);
  const requestFetchedRef = useRef(false);

  const addTab = () => {
    const keyNumbers = tabs
      .filter((tab) => tab.key.startsWith("TAB"))
      .map((tab) => parseInt(tab.key.replace("TAB", ""), 10))
      .filter((num) => !isNaN(num));

    const nextKeyNumber = Math.max(0, ...keyNumbers) + 1;
    const newTabKey = `TAB${nextKeyNumber}`;

    const labelNumbers = tabs
      .filter((tab) => /^TAB\d+$/i.test(tab.label))
      .map((tab) => parseInt(tab.label.replace("TAB", ""), 10))
      .filter((num) => !isNaN(num));

    let newLabelNumber = 1;
    while (labelNumbers.includes(newLabelNumber)) {
      newLabelNumber++;
    }

    const newLabel = `TAB${newLabelNumber}`;

    const updatedTabs = [
      ...tabs,
      {
        key: newTabKey,
        label: newLabel,
        values: { ...initialValues },
      },
    ];

    const nonTabTabs = updatedTabs.filter(
      (tab) => !/^TAB\d+$/i.test(tab.label)
    );
    const tabTabs = updatedTabs
      .filter((tab) => /^TAB\d+$/i.test(tab.label))
      .sort((a, b) => {
        const aNum = parseInt(a.label.replace("TAB", ""), 10);
        const bNum = parseInt(b.label.replace("TAB", ""), 10);
        return aNum - bNum;
      });

    setTabs([...nonTabTabs, ...tabTabs]);
  };

  const deleteTab = (tabKey) => {
    const updatedTabs = tabs.filter((tab) => tab.key !== tabKey);

    setTabs(updatedTabs);
    handleSaveDraft(false, updatedTabs);
  };

  const updateTabValues = (tabKey, newValues) => {
    setTabs((prevTabs) =>
      prevTabs.map((tab) =>
        tab.key === tabKey
          ? {
              ...tab,
              values: { ...tab.values, ...newValues },
              label: newValues.insurance || tab.label.toUpperCase(), // Update label with insurance name if provided
            }
          : tab
      )
    );
  };

  const updateQuotation = async (id) => {
    const tab = tabs.find((tab) => tab.values.id === id);
    const values = tab.values;

    const formattedAddOns = values.add_ons
      .filter((addOn) => addOn.add_on_name && addOn.add_on_price)
      .map((addOn) => ({
        add_on_name: addOn.add_on_name,
        add_on_price: parseFloat(addOn.add_on_price),
      }));

    const response = await updateQuoteQuotation({
      id: id,
      quotation: {
        insurance_provider: values.insurance,
        plan_name: values.plan_name,
        policy_wording_document: values.policy_wording,
        product_disclosure_sheet: values.product_disclosure_sheet,
        price: values.premium_price.toString(),
        sum_insured: values.sum_insured.toString(),
        ncd_rate: Number(values.ncd_rate),
      },
      plan_benefits: values.plan_benefits.map((value) => ({
        plan_benefit: value,
      })),
      add_ons: formattedAddOns,
    });

    if (response.success) {
      toast.success("Quotations updated successfully");

      // set back to prefilled
      setTabs((prevTabs) =>
        prevTabs.map((tab) =>
          tab.values.id === id
            ? {
                ...tab,
                isPrefilled: false,
              }
            : tab
        )
      );
    } else {
      toast.error("Failed to update quotations");
    }
  };

  const getMotorPredefinedData = async (
    insurance_name,
    page = 1,
    perPage = 10
  ) => {
    // setIsLoading(true);
    try {
      if (!insurance_name) {
        setPlanNameField([]);
        return;
      }

      const response = await getMotorPredefined({
        insurance_name,
        page,
        perPage,
      });

      if (response.success) {
        setPlanNameField(
          response.data.data
            .filter((item) => item.plan_status !== 0) // ignore if plan_status is 0
            .map((item) => item.plan_name)
        );
      } else {
        toast.error("Failed to fetch data");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("An error occurred while fetching data");
    } finally {
      setIsLoading(false);
    }
  };

  const findMotorPredefinedData = async () => {
    try {
      if (requestFetchedRef.current) {
        console.log("requestResponse skipped");
      } else {
        const requestResponse = await getQuoteRequest(id);
        requestFetchedRef.current = true;

        if (requestResponse.data.draft) return;
      }

      const emptyValues = {
        id: "",
        insurance: insurance_name,
        plan_name: "",
        policy_wording: "",
        product_disclosure_sheet: "",
        premium_price: "",
        sum_insured: "",
        ncd_rate: "",
        add_ons: [{ add_on_name: "", add_on_price: "" }],
        plan_benefits: [""],
      };

      if (insurance_name === "" || plan_name === "") {
        const activeTabData = tabs.find((tab) => tab.key === activeTab);

        const prefilledTabs = {
          key: `prefilledTab-${Date.now()}`,
          label: insurance_name || activeTabData.key,
          values: emptyValues,
        };

        setTabs((prevTabs) =>
          prevTabs.map((tab) => {
            if (tab.key === activeTab) {
              return {
                ...prefilledTabs,
                key: activeTab,
              };
            }
            return tab;
          })
        );
        return;
      }

      const response = await findMotorPredefined({ insurance_name, plan_name });

      const activeTabData = tabs.find((tab) => tab.key === activeTab);

      const filledValues = {
        id: "",
        insurance: response.data.insurance_name,
        plan_name: response.data.plan_name,
        policy_wording: response.data.policy_wording_url,
        product_disclosure_sheet: response.data.product_disclosure_sheet_url,
        premium_price: activeTabData.values.premium_price,
        sum_insured: activeTabData.values.sum_insured,
        ncd_rate: "",
        add_ons:
          response.data.add_ons.length > 0
            ? response.data.add_ons.map((addOn) => ({
                add_on_name: addOn.add_on_name,
                add_on_price: addOn.add_on_price,
              }))
            : [{ add_on_name: "", add_on_price: "" }],
        plan_benefits: Array.isArray(response.data.plan_benefits)
          ? response.data.plan_benefits.map((item) =>
              typeof item === "object" ? item.benefit_description : item
            )
          : [""],
      };

      const prefilledTabs = {
        key: `prefilledTab-${Date.now()}`,
        label: `${response.data.insurance_name}`,
        values:
          response.data.plan_status === 0 || plan_name === ""
            ? emptyValues
            : filledValues,
      };

      setTabs((prevTabs) => {
        return prevTabs.map((tab) => {
          if (tab.key === activeTab) {
            return {
              ...prefilledTabs,
              key: activeTab,
            };
          }
          return tab;
        });
      });
    } catch (error) {}
  };

  const insuranceProviders = [
    "AIA GENERAL BERHAD",
    "AIG MALAYSIA INSURANCE BERHAD",
    "ALLIANZ GENERAL INSURANCE COMPANY (MALAYSIA) BERHAD",
    "BERJAYA SOMPO INSURANCE BERHAD",
    "CHUBB INSURANCE MALAYSIA BERHAD",
    "ETIQA GENERAL INSURANCE BERHAD",
    "ETIQA GENERAL TAKAFUL BERHAD",
    "GENERALI INSURANCE BERHAD",
    "GREAT EASTERN GENERAL INSURANCE (MALAYSIA) BERHAD",
    "LIBERTY GENERAL INSURANCE BERHAD",
    "LONPAC INSURANCE BERHAD",
    "MSIG INSURANCE (MALAYSIA) BERHAD",
    "PACIFIC AND ORIENT INSURANCE COMPANY BERHAD",
    "QBE INSURANCE (MALAYSIA) BERHAD",
    "RHB INSURANCE BERHAD",
    "SYARIKAT TAKAFUL MALAYSIA AM BERHAD",
    "TAKAFUL IKHLAS GENERAL BERHAD",
    "THE PACIFIC INSURANCE BERHAD",
    "TOKIO MARINE INSURANCE (MALAYSIA) BERHAD",
    "TUNE INSURANCE MALAYSIA BERHAD",
    "ZURICH GENERAL INSURANCE MALAYSIA BERHAD",
    "ZURICH GENERAL TAKAFUL MALAYSIA BERHAD",
  ];

  useEffect(() => {
    const activeTabData = tabs.find((tab) => tab.key === activeTab);
    getMotorPredefinedData(activeTabData?.values?.insurance);
  }, [activeTab, tabs]);

  const formDisabled = isLocked;

  const fetchData = useCallback(async (id) => {
    setIsLoading(true);
    try {
      const response = await getQuoteRequest(id);
      if (response.success) {
        const data = response.data;

        let hasDraft = false;

        if (data.draft) {
          hasDraft = true;
          const draft = data.draft;

          setTabs(draft.tabs);
          setActiveTab(draft.activeTab || draft.tabs[0].key);

          const activeTabData = draft.tabs.find(
            (tab) => tab.key === draft.activeTab
          );
          setInsuranceName(activeTabData?.values?.insurance);
          setPlanName(activeTabData?.values?.plan_name);
        }

        setOrder(data);
        setIsLocked(data.order_status === "Quotation is Ready");

        // STOP prefill tabs if draft exists
        if (hasDraft) return;

        const prefilledTabs =
          data.motor_insurance_quotations?.map((quote, index) => ({
            key: `prefilledTab${index + 1}-${quote.id || Date.now()}`, // Ensure unique key
            label: `${quote.insurance_provider}`, // Only display insurer name
            values: {
              id: quote.id,
              insurance: quote.insurance_provider,
              plan_name: quote.plan_name,
              policy_wording: quote.policy_wording_document,
              product_disclosure_sheet: quote.product_disclosure_sheet,
              premium_price: quote.price,
              sum_insured: quote.sum_insured,
              ncd_rate: data.quote_request?.ncd_percent ?? quote.ncd_rate ?? "",
              add_ons: quote.add_ons.map((addOn) => ({
                add_on_name: addOn.add_on_name,
                add_on_price: addOn.add_on_price,
              })),
              plan_benefits: quote.plan_benefits.map(
                (benefit) => benefit.plan_benefit
              ),
            },
            isPrefilled: false, // Mark as prefilled
          })) || [];

        setTabs([
          ...prefilledTabs,
          { key: "tab1", label: "Tab 1", values: { ...initialValues } },
        ]);
        //setIsLoading(false);
      } else {
        toast.error("Failed to fetch order details");
        setOrder({});
      }
    } catch (error) {
      toast.error("Error fetching data");
      console.log(error);
      setOrder({});
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    findMotorPredefinedData();
  }, [plan_name]);

  useEffect(() => {
    if (id) {
      fetchData(id);
    }
  }, [id, fetchData]);

  const validationSchema = Yup.object().shape({
    insurance: Yup.string().required("Insurance name is required"),
    plan_name: Yup.string().required("Plan name is required"),
    policy_wording: Yup.string()
      .required("Policy Wording Document (URL) is required")
      .url("Please enter a valid URL"),
    product_disclosure_sheet: Yup.string()
      .required("Product Disclosure Sheet (URL) is required")
      .url("Please enter a valid URL"),
    premium_price: Yup.number()
      .required("Basic Premium is required")
      .positive("Basic Premium must be positive")
      .test(
        "is-decimal",
        "Basic Premium must have exactly 2 decimal places",
        (value) => /^\d+(\.\d{1,2})?$/.test(value)
      ),
    sum_insured: Yup.number()
      .required("Sum insured is required")
      .positive("Sum insured must be positive")
      .test(
        "is-decimal",
        "Sum insured must have exactly 2 decimal places",
        (value) => /^\d+(\.\d{1,2})?$/.test(value)
      ),
    ncd_rate: Yup.string().required("No Claim Discount (NCD) rate is required"),
    plan_benefits: Yup.array()
      .of(Yup.string().required("Benefit is required"))
      .min(1, "At least 1 benefit is required"),
    add_ons: Yup.array()
      .of(
        Yup.object().shape({
          add_on_name: Yup.string().trim().optional(),
          add_on_price: Yup.number()
            .typeError("Add-on price must be a number")
            .positive("Add-on price must be positive")
            .test(
              "is-decimal",
              "Add-on price must have exactly 2 decimal places",
              (value) => value === undefined || /^\d+(\.\d{1,2})?$/.test(value)
            )
            .optional(),
        })
      )
      .optional()
      .test(
        "valid-add-ons",
        "Add-ons must have both name and price if one is provided",
        (addOns) => {
          if (!addOns || addOns.length === 0) return true; // Allow empty array
          return addOns.every((addOn) => {
            const hasName =
              addOn.add_on_name && addOn.add_on_name.trim() !== "";
            const hasPrice =
              addOn.add_on_price !== undefined && addOn.add_on_price !== null;
            return (!hasName && !hasPrice) || (hasName && hasPrice);
          });
        }
      ),
  });

  const handleSubmit = async (formikRefs) => {
    setIsSubmittingForm(true);
    let hasErrors = false;

    try {
      const newTabs = tabs.filter((tab) => !tab.isPrefilled && !tab.values.id); // Filter out prefilled tabs

      if (tabs.length === 0) {
        toast.info("Please add at least one insurance before submitting.");
        setIsSubmittingForm(false);
        return;
      }

      for (const [index, tab] of tabs.entries()) {
        if (!tab.isPrefilled) {
          // Only validate non-prefilled tabs
          const formik = formikRefs[index];
          if (formik) {
            const validationErrors = await formik.validateForm(); // Wait for validation to complete

            formik.setTouched(
              Object.keys(tab.values).reduce((acc, key) => {
                if (Array.isArray(tab.values[key])) {
                  acc[key] =
                    key === "add_ons"
                      ? tab.values[key].map(() => ({ add_on_name: true, add_on_price: true }))
                      : tab.values[key].map(() => true);
                } else {
                  acc[key] = true;
                }
                return acc;
              }, {})
            );

            if (Object.keys(validationErrors).length > 0) {
              console.log(validationErrors);
              hasErrors = true; // Mark as having errors if validationErrors is not empty
            }
          }
        }
      }

      if (hasErrors) {
        toast.error("Please fix validation errors before submitting.");
        return;
      }

      if (
        !order ||
        !order.order_details?.enquiry_id ||
        isNaN(order.order_details?.enquiry_id)
      ) {
        throw new Error("Order data is not available or invalid");
      }

      const payload = {
        enquiry_id: Number(order.order_details?.enquiry_id),
        quotations: newTabs.map((tab) => {
          const values = tab.values;
          const formattedAddOns = values.add_ons
            .filter((addOn) => addOn.add_on_name && addOn.add_on_price)
            .map((addOn) => ({
              add_on_name: addOn.add_on_name,
              add_on_price: parseFloat(addOn.add_on_price),
            }));

          return {
            insurance_provider: values.insurance,
            plan_name: values.plan_name,
            policy_wording_document: values.policy_wording,
            product_disclosure_sheet: values.product_disclosure_sheet,
            price: values.premium_price.toString(),
            sum_insured: values.sum_insured.toString(),
            ncd_rate: Number(values.ncd_rate),
            plan_benefits: values.plan_benefits,
            add_ons: formattedAddOns,
          };
        }),
      };

      const response = await uploadQuoteQuotation(payload);
      if (response.success) {
        toast.success("Quotations created successfully!");
        const updateResponse = await updateQuoteRequest({
          id: order.order_details?.order_id,
          order_status: "Quotation is Ready",
        });
        if (updateResponse.success) {
          setOrder((prevOrder) => ({
            ...prevOrder,
            order_status: "Quotation is Ready",
          }));
          navigate("/requests");
        } else {
          throw new Error("Failed to update order status");
        }
      } else {
        throw new Error("Failed to create quotations");
      }
    } catch (error) {
      if (error.name !== "ValidationError") {
        console.error("Error during submission:", error);
        toast.error(
          error.message || "Failed to create quotations. Please try again."
        );
      }
    } finally {
      setIsSubmittingForm(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    const updatedStatus =
      newStatus.charAt(0).toUpperCase() + newStatus.slice(1);

    const originalStatus = order.order_status;

    if (originalStatus === updatedStatus) {
      return;
    }

    setOrder((prevOrder) => ({
      ...prevOrder,
      order_status: updatedStatus,
    }));

    try {
      const response = await updateQuoteRequest({
        id: orderId, // Updated to use order_details.order_id
        order_status: newStatus,
      });
      if (response.success) {
        toast.success("Request status updated successfully");
      } else {
        throw new Error(response.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Error updating status");
      setOrder((prevOrder) => ({
        ...prevOrder,
        order_status: originalStatus,
      }));
    }
  };

  const ncdRates = [0, 15, 20, 25, 30, 38.33, 45, 55];

  const formatNumberWithCommas = (value) => {
    if (!value) return "";
    const parts = value.toString().split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.join(".");
  };

  const formatToTwoDecimals = (value) => {
    if (!value) return "";
    const rawValue = value.replace(/,/g, "");
    const match = rawValue.match(/^\d*(\.\d{0,2})?/);
    return match ? match[0] : "";
  };

  const handleSaveDraft = async (showToast = true, updatedTabs = tabs) => {
    try {
      setIsSavingDraft(true);

      const payload = {
        enquiry_id: order.order_details?.enquiry_id,
        updated_by: 999, // later set admin id
        draft_data: { tabs: updatedTabs, activeTab },
      };

      const response = await saveDraft(payload);

      if (response.success) {
        if (showToast) toast.success("Draft saved");
      } else {
        toast.error("Failed to save draft");
      }
    } catch (err) {
      toast.error("Failed to save draft");
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleRevertStatus = async () => {
    const response = await revertStatus({
      order_id: order.order_details?.order_id,
    });

    if (response.success) {
      toast.success("Status reverted. Editing enabled.");
      setIsLocked(false);
      setOrder((prev) => ({
        ...prev,
        order_status: "Pending Quotation",
      }));
    }
  };

  return (
    <>
      {isLoading ? (
        <div className="card border-light">
          <div className="card-body p-4">
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
              <div className="col-md-6 mb-3">
                <Skeleton height={20} width={150} />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <Skeleton
                height={40}
                width={100}
                style={{ marginRight: "10px" }}
              />
              <Skeleton height={40} width={100} />
            </div>
          </div>
        </div>
      ) : (
        <div className="card border-light">
          <div className="card-body p-4">
            <label className="mb-4">Personal Information Details</label>
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">User ID:</label>
                <p>
                  <b>{order.user_details?.id || "N/A"}</b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Owner Name:</label>
                <p>
                  <b>{order.quote_request?.policy_holder_name || "N/A"}</b>
                </p>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">ID Type:</label>
                <p>
                  <b>{order.quote_request?.id_type || "N/A"}</b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">ID No:</label>
                <p>
                  <b>{order.quote_request?.id_no || "N/A"}</b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Postcode:</label>
                <p>
                  <b>{order.quote_request?.postcode || "N/A"}</b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Vehicle Type:</label>
                <p>
                  <b>
                    {order.quote_request?.veh_type?.replace(/^./, (c) =>
                      c.toUpperCase()
                    ) || "N/A"}
                  </b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Email Address:</label>
                <p>
                  <b>
                    {order.quote_request?.email ||
                      (order.user_details?.email === "jpjredirect@ansa.my"
                        ? "N/A"
                        : order.user_details?.email || "N/A")}
                  </b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Contact Number:</label>
                <p>
                  <b>
                    {order.quote_request?.hphoneno ||
                      (order.user_details?.email === "jpjredirect@ansa.my"
                        ? "N/A"
                        : order.user_details?.phone_number || "N/A")}
                  </b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Marital Status:</label>
                <p>
                  <b>
                    {(order.quote_request?.marital_code === "M"
                      ? "Married"
                      : order.quote_request?.marital_code === "S"
                      ? "Single"
                      : order.quote_request?.marital_code === "D"
                      ? "Divorced"
                      : order.quote_request?.marital_code === "W"
                      ? "Widowed"
                      : "Others") || "N/A"}
                  </b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Company Name:</label>
                <p>
                  <b>{order.quote_request?.company_name || "N/A"}</b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Vehicle No.:</label>
                <p>
                  <b>{order.quote_request?.veh_reg_no || "N/A"}</b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Created At:</label>
                <p>
                  <b>
                    {order.created_at
                      ? formatDateTime(order.created_at)
                      : "N/A"}
                  </b>
                </p>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Request Status:</label>
                <p>
                  <CDropdown>
                    <CDropdownToggle
                      color="secondary"
                      variant="outline"
                      style={{
                        backgroundColor:
                          order.order_status === "Pending Quotation"
                            ? "#A9A9A9" // Orange
                            : order.order_status === "Quotation is Ready"
                            ? "#66BB6A" // Green
                            : order.order_status === "Closed"
                            ? "#000000" // Black
                            : "#FFFFFF", // Default
                        color: "#fff",
                        border: "none",
                        width: "160px",
                        fontSize: "14px",
                        borderRadius: "20px",
                        fontWeight: "500",
                      }}
                    >
                      {order.order_status
                        ? order.order_status.charAt(0).toUpperCase() +
                          order.order_status.slice(1)
                        : "N/A"}
                    </CDropdownToggle>
                    <CDropdownMenu>
                      <CDropdownItem
                        onClick={() =>
                          handleStatusChange(
                            order.order_details?.order_id,
                            "Pending Quotation"
                          )
                        }
                      >
                        Pending Quotation
                      </CDropdownItem>
                      <CDropdownItem
                        onClick={() =>
                          handleStatusChange(
                            order.order_details?.order_id,
                            "Quotation is Ready"
                          )
                        }
                      >
                        Quotation is Ready
                      </CDropdownItem>
                      <CDropdownItem
                        onClick={() =>
                          handleStatusChange(
                            order.order_details?.order_id,
                            "Closed"
                          )
                        }
                      >
                        Closed
                      </CDropdownItem>
                    </CDropdownMenu>
                  </CDropdown>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">NCD:</label>
                <p>
                  <b>
                    {order.quote_request?.ncd_percent !== null
                      ? Math.floor(order.quote_request?.ncd_percent) + "%"
                      : "N/A"}
                  </b>
                </p>
              </div>
              <div className="col-md-4 mb-3">
                <label className="form-label">Preferred Insurances:</label>
                <p>
                  <b>
                    {Array.isArray(order.quote_preferred_insurers) &&
                    order.quote_preferred_insurers.length > 0
                      ? order.quote_preferred_insurers
                          .map((item) => item.insurer_name)
                          .join(", ")
                      : "N/A"}
                  </b>
                </p>
              </div>
            </div>
            <label className="mb-1s d-flex justify-content-between align-items-center">
              Quotation Details
            </label>
            {isLocked && (
              <div className="alert alert-warning d-flex justify-content-between align-items-center">
                <span>
                  This quotation is locked. Revert to <b>Pending Quotation</b>{" "}
                  to edit.
                </span>

                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleRevertStatus}
                >
                  Revert to Pending Quotation
                </button>
              </div>
            )}
            <CTabs activeItemKey={activeTab} onChange={setActiveTab}>
              <CTabList variant="underline" className="mb-3">
                {tabs.map((tab, index) => (
                  <div key={tab.key} className="d-flex align-items-center">
                    <CTab itemKey={tab.key}>{tab.label}</CTab>
                    {tab.values.id && (
                      <DeleteQuotationModal
                        id={tab.values.id}
                        index={index}
                        setTabs={setTabs}
                      />
                    )}
                    {!tab.isPrefilled && !tab.values.id && tabs.length > 1 && (
                      <div
                        className="btn btn-sm btn-danger text-white"
                        style={{
                          marginLeft: "8px",
                          cursor: "pointer",
                          transition: "background-color 0.2s",
                        }}
                        onClick={() => deleteTab(tab.key)}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.backgroundColor = "#e0e0e0")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.backgroundColor = "#dc3545")
                        }
                        onMouseDown={(e) =>
                          (e.currentTarget.style.backgroundColor = "#d0d0d0")
                        }
                        onMouseUp={(e) =>
                          (e.currentTarget.style.backgroundColor = "#e0e0e0")
                        }
                      >
                        <i
                          className="bi bi-trash-fill"
                          style={{
                            transition: "color 0.2s",
                          }}
                          onMouseEnter={(e) =>
                            (e.target.style.color = "#ff4d4d")
                          }
                          onMouseLeave={(e) =>
                            (e.target.style.color = "#ffffff")
                          }
                          onMouseDown={(e) =>
                            (e.target.style.color = "#cc0000")
                          }
                          onMouseUp={(e) => (e.target.style.color = "#ff4d4d")}
                        />
                      </div>
                    )}
                  </div>
                ))}
                {/* Add button to create a new tab on the right */}
                {!isLocked && (
                  <button
                    type="button"
                    className="btn btn-link"
                    style={{
                      textDecoration: "none",
                      color: "#0B4FE4",
                      marginLeft: "8px",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                    onClick={addTab}
                    onBlur={() => {
                      handleSaveDraft(false);
                    }}
                  >
                    + Add More Insurance
                  </button>
                )}
              </CTabList>
              <CTabContent>
                {tabs.map((tab, index) => (
                  <CTabPanel
                    key={tab.key}
                    itemKey={tab.key}
                    className=""
                    style={{ margin: "", borderRadius: "10px" }}
                  >
                    <Formik
                      innerRef={(ref) => (formikRefs.current[index] = ref)} // Store Formik instance for each tab
                      enableReinitialize={true}
                      initialValues={{
                        // Include everything in tab.values
                        ...tab.values,
                        // But override ncd_rate with quote_request value if available
                        ncd_rate:
                          order.quote_request?.ncd_percent ??
                          tab.values?.ncd_rate ??
                          "",
                      }}
                      validationSchema={
                        !tab.isPrefilled ? validationSchema : null
                      } // Skip validation for prefilled tabs
                      onSubmit={(values) => updateTabValues(tab.key, values)}
                    >
                      {({
                        isSubmitting,
                        errors,
                        touched,
                        setFieldValue,
                        values,
                      }) => (
                        <Form>
                          {isSubmittingForm && (
                            <div className="form-overlay">
                              <CSpinner color="primary" />
                            </div>
                          )}
                          {errors.submit && (
                            <div className="alert alert-danger">
                              {errors.submit}
                            </div>
                          )}
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <CFormLabel htmlFor="insurance">
                                <span className="text-danger">*</span> Insurance
                                Name
                              </CFormLabel>
                              <Field
                                name="insurance"
                                as="select"
                                className={`form-control ${
                                  touched.insurance && errors.insurance
                                    ? "is-invalid"
                                    : ""
                                }`}
                                disabled={formDisabled || tab.isPrefilled} // Disable for prefilled tabs
                                onChange={(e) => {
                                  setFieldValue("insurance", e.target.value);
                                  updateTabValues(tab.key, {
                                    insurance: e.target.value,
                                  });
                                  setInsuranceName(e.target.value);
                                  getMotorPredefinedData(e.target.value);
                                  setPlanName("");
                                }}
                                onBlur={() => {
                                  handleSaveDraft(false);
                                }}
                              >
                                <option value="">Select Insurance Name</option>
                                {tab.isPrefilled && values.insurance && (
                                  <option value={values.insurance}>
                                    {values.insurance}
                                  </option>
                                )}
                                {insuranceProviders.map((provider, index) => (
                                  <option key={index} value={provider}>
                                    {provider}
                                  </option>
                                ))}
                              </Field>
                              {touched.insurance && errors.insurance && (
                                <div className="invalid-feedback d-block mt-1">
                                  {errors.insurance}
                                </div>
                              )}
                            </div>
                            {planNameField && planNameField.length > 0 ? (
                              <div className="col-md-6 mb-3">
                                <label
                                  htmlFor="plan_name"
                                  className="form-label"
                                >
                                  <span className="text-danger">*</span> Plan
                                  Name
                                </label>
                                <Field
                                  name="plan_name"
                                  as="select"
                                  type="text"
                                  className={`form-control ${
                                    touched.plan_name && errors.plan_name
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  disabled={formDisabled || tab.isPrefilled} // Disable for prefilled tabs
                                  onChange={(e) => {
                                    setFieldValue("plan_name", e.target.value);
                                    updateTabValues(tab.key, {
                                      plan_name: e.target.value,
                                    });
                                    setPlanName(e.target.value);
                                  }}
                                  onBlur={(e) => {
                                    handleSaveDraft(false);
                                  }}
                                >
                                  <option value="">Select Plan Name</option>
                                  {planNameField.map((planName, index) => (
                                    <option key={index} value={planName}>
                                      {planName}
                                    </option>
                                  ))}
                                </Field>
                                {touched.plan_name && errors.plan_name && (
                                  <div className="invalid-feedback d-block mt-1">
                                    {errors.plan_name}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="col-md-6 mb-3">
                                <label
                                  htmlFor="plan_name"
                                  className="form-label"
                                >
                                  <span className="text-danger">*</span> Plan
                                  Name
                                </label>
                                <Field
                                  name="plan_name"
                                  as="input"
                                  type="text"
                                  className={`form-control ${
                                    touched.plan_name && errors.plan_name
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  disabled={formDisabled || tab.isPrefilled} // Disable for prefilled tabs
                                  onChange={(e) => {
                                    setFieldValue("plan_name", e.target.value);
                                    updateTabValues(tab.key, {
                                      plan_name: e.target.value,
                                    });
                                  }}
                                  onBlur={(e) => {
                                    handleSaveDraft(false);
                                    setPlanName(e.target.value);
                                  }}
                                />
                                {touched.plan_name && errors.plan_name && (
                                  <div className="invalid-feedback d-block mt-1">
                                    {errors.plan_name}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <label
                                htmlFor="policy_wording"
                                className="form-label"
                              >
                                <span className="text-danger">*</span> Policy
                                Wording Document (URL)
                              </label>
                              <Field
                                name="policy_wording"
                                as="input"
                                type="text"
                                className={`form-control ${
                                  touched.policy_wording &&
                                  errors.policy_wording
                                    ? "is-invalid"
                                    : ""
                                }`}
                                placeholder="Enter URL link"
                                disabled={formDisabled || tab.isPrefilled} // Disable for prefilled tabs
                                onChange={(e) => {
                                  setFieldValue(
                                    "policy_wording",
                                    e.target.value
                                  );
                                  updateTabValues(tab.key, {
                                    policy_wording: e.target.value,
                                  });
                                }}
                                onBlur={() => {
                                  handleSaveDraft(false);
                                }}
                              />
                              {touched.policy_wording &&
                                errors.policy_wording && (
                                  <div className="invalid-feedback d-block mt-1">
                                    {errors.policy_wording}
                                  </div>
                                )}
                            </div>
                            <div className="col-md-6 mb-3">
                              <label
                                htmlFor="product_disclosure_sheet"
                                className="form-label"
                              >
                                <span className="text-danger">*</span> Product
                                Disclosure Sheet (URL)
                              </label>
                              <Field
                                name="product_disclosure_sheet"
                                as="input"
                                type="text"
                                className={`form-control ${
                                  touched.product_disclosure_sheet &&
                                  errors.product_disclosure_sheet
                                    ? "is-invalid"
                                    : ""
                                }`}
                                placeholder="Enter URL link"
                                disabled={formDisabled || tab.isPrefilled} // Disable for prefilled tabs
                                onChange={(e) => {
                                  setFieldValue(
                                    "product_disclosure_sheet",
                                    e.target.value
                                  );
                                  updateTabValues(tab.key, {
                                    product_disclosure_sheet: e.target.value,
                                  });
                                }}
                                onBlur={() => {
                                  handleSaveDraft(false);
                                }}
                              />
                              {touched.product_disclosure_sheet &&
                                errors.product_disclosure_sheet && (
                                  <div className="invalid-feedback d-block mt-1">
                                    {errors.product_disclosure_sheet}
                                  </div>
                                )}
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-md-6 mb-3">
                              <CFormLabel htmlFor="premium_price">
                                <span className="text-danger">*</span> Basic
                                Premium
                              </CFormLabel>
                              <div className="position-relative">
                                <Field
                                  id="premium_price"
                                  name="premium_price"
                                  type="text"
                                  placeholder="Enter Basic Premium"
                                  className={`form-control ps-5 ${
                                    touched.premium_price &&
                                    errors.premium_price
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  value={formatNumberWithCommas(
                                    values.premium_price
                                  )}
                                  disabled={formDisabled || tab.isPrefilled} // Disable for prefilled tabs
                                  onChange={(e) => {
                                    const formattedValue = formatToTwoDecimals(
                                      e.target.value
                                    );
                                    setFieldValue(
                                      "premium_price",
                                      formattedValue
                                    );
                                    updateTabValues(tab.key, {
                                      premium_price: formattedValue,
                                    });
                                  }}
                                  onBlur={() => {
                                    handleSaveDraft(false);
                                  }}
                                />
                                <span
                                  className="position-absolute top-50 start-0 translate-middle-y ms-2"
                                  style={{ color: "#6c757d" }}
                                >
                                  RM
                                </span>
                              </div>
                              {touched.premium_price &&
                                errors.premium_price && (
                                  <div className="invalid-feedback d-block mt-1">
                                    {errors.premium_price}
                                  </div>
                                )}
                            </div>
                            <div className="col-md-6 mb-3">
                              <CFormLabel htmlFor="sum_insured">
                                <span className="text-danger">*</span> Sum
                                Insured
                              </CFormLabel>
                              <div className="position-relative">
                                <Field
                                  id="sum_insured"
                                  name="sum_insured"
                                  type="text"
                                  placeholder="Enter Sum Insured"
                                  className={`form-control ps-5 ${
                                    touched.sum_insured && errors.sum_insured
                                      ? "is-invalid"
                                      : ""
                                  }`}
                                  value={formatNumberWithCommas(
                                    values.sum_insured
                                  )}
                                  disabled={formDisabled || tab.isPrefilled} // Disable for prefilled tabs
                                  onChange={(e) => {
                                    const formattedValue = formatToTwoDecimals(
                                      e.target.value
                                    );
                                    setFieldValue(
                                      "sum_insured",
                                      formattedValue
                                    );
                                    updateTabValues(tab.key, {
                                      sum_insured: formattedValue,
                                    });
                                  }}
                                  onBlur={() => {
                                    handleSaveDraft(false);
                                  }}
                                />
                                <span
                                  className="position-absolute top-50 start-0 translate-middle-y ms-2"
                                  style={{ color: "#6c757d" }}
                                >
                                  RM
                                </span>
                              </div>
                              {touched.sum_insured && errors.sum_insured && (
                                <div className="invalid-feedback d-block mt-1">
                                  {errors.sum_insured}
                                </div>
                              )}
                            </div>
                            <div className="col-md-6 mb-3">
                              <CFormLabel htmlFor="ncd_rate">
                                <span className="text-danger">*</span> No Claim
                                Discount (NCD) Rate
                              </CFormLabel>
                              <Field
                                name="ncd_rate"
                                as="select"
                                className={`form-control ${
                                  touched.ncd_rate && errors.ncd_rate
                                    ? "is-invalid"
                                    : ""
                                }`}
                                disabled={
                                  values.ncd_rate && tab.isPrefilled
                                    ? true
                                    : false
                                } // Disable for prefilled tabs
                                onChange={(e) => {
                                  setFieldValue("ncd_rate", e.target.value);
                                  updateTabValues(tab.key, {
                                    ncd_rate: e.target.value,
                                  });
                                }}
                                onBlur={() => {
                                  handleSaveDraft(false);
                                }}
                              >
                                <option value="">Select NCD Rate</option>
                                {order.quote_request?.ncd_percent !== null &&
                                  order.quote_request?.ncd_percent !==
                                    undefined && (
                                    <option
                                      value={order.quote_request.ncd_percent}
                                    >
                                      {order.quote_request.ncd_percent}%
                                    </option>
                                  )}

                                {/* fallback for prefilled tab */}
                                {tab.isPrefilled &&
                                  !order.quote_request?.ncd_percent &&
                                  values.ncd_rate && (
                                    <option value={values.ncd_rate}>
                                      {values.ncd_rate}%
                                    </option>
                                  )}
                                {ncdRates.map((rate, index) => (
                                  <option key={index} value={rate}>
                                    {rate}%
                                  </option>
                                ))}
                              </Field>
                              {touched.ncd_rate && errors.ncd_rate && (
                                <div className="invalid-feedback d-block mt-1">
                                  {errors.ncd_rate}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="row">
                            <div className="col-md-12 mb-3">
                              <span className="text-danger">*</span>{" "}
                              <label
                                htmlFor="plan_benefits"
                                className="form-label"
                              >
                                Plan Benefits
                                <CTooltip content="Add up to 10 benefits">
                                  <span
                                    className="ms-2"
                                    style={{ marginLeft: "5px" }}
                                  >
                                    <i className="bi bi-info-circle"></i>
                                  </span>
                                </CTooltip>
                              </label>
                              <FieldArray name="plan_benefits">
                                {({ push, remove, form }) => (
                                  <>
                                    {form.values.plan_benefits &&
                                      form.values.plan_benefits.length === 0 &&
                                      !tab.isPrefilled && (
                                        <div className="d-flex justify-content-start mb-2">
                                          <button
                                            type="button"
                                            className="btn d-flex align-items-center"
                                            style={{
                                              backgroundColor: "transparent",
                                              color: "#0B4FE4",
                                            }}
                                            onClick={() => push("")}
                                          >
                                            <span className="me-2">+</span> Add
                                            Benefit
                                          </button>
                                        </div>
                                      )}
                                    {form.values.plan_benefits &&
                                      form.values.plan_benefits.map(
                                        (benefit, index) => (
                                          <div key={index} className="mb-2">
                                            <div className="position-relative">
                                              <input
                                                id={`plan_benefits.${index}`}
                                                name={`plan_benefits.${index}`}
                                                type="text"
                                                placeholder="Enter Benefit"
                                                maxLength={120}
                                                className={`form-control pe-5 ${
                                                  touched.plan_benefits?.[
                                                    index
                                                  ] &&
                                                  errors.plan_benefits?.[index]
                                                    ? "is-invalid"
                                                    : ""
                                                }`}
                                                disabled={
                                                  formDisabled ||
                                                  tab.isPrefilled
                                                } // Disable for prefilled tabs
                                                onChange={(e) => {
                                                  setFieldValue(
                                                    `plan_benefits.${index}`,
                                                    e.target.value
                                                  );
                                                  updateTabValues(tab.key, {
                                                    plan_benefits:
                                                      values.plan_benefits.map(
                                                        (b, i) =>
                                                          i === index
                                                            ? e.target.value
                                                            : b
                                                      ),
                                                  });
                                                }}
                                                onBlur={() => {
                                                  handleSaveDraft(false);
                                                }}
                                                value={benefit}
                                              />
                                              <div
                                                className="position-absolute top-50 end-0 translate-middle-y d-flex align-items-center"
                                                style={{
                                                  gap: "8px",
                                                  marginRight: "1em", // Default margin
                                                  ...(touched.plan_benefits?.[
                                                    index
                                                  ] &&
                                                  errors.plan_benefits?.[index]
                                                    ? { marginRight: "1.9rem" }
                                                    : {}),
                                                }}
                                              >
                                                <span className="text-muted">
                                                  {benefit.length}/120
                                                </span>
                                                {!tab.isPrefilled &&
                                                  form.values.plan_benefits
                                                    .length > 1 && (
                                                    <CButton
                                                      className="btn btn-sm btn-danger text-white"
                                                      style={{
                                                        padding: "2px 6px",
                                                        fontSize: "0.75rem",
                                                      }}
                                                      title="Delete"
                                                      onClick={() => {
                                                        remove(index);
                                                      }}
                                                    >
                                                      <i className=" bi bi-trash-fill"></i>
                                                    </CButton>
                                                  )}
                                              </div>
                                            </div>

                                            {touched.plan_benefits?.[index] &&
                                              errors.plan_benefits?.[index] && (
                                                <div className="invalid-feedback d-block mt-1">
                                                  {errors.plan_benefits[index]}
                                                </div>
                                              )}
                                          </div>
                                        )
                                      )}
                                    {form.values.plan_benefits &&
                                      form.values.plan_benefits.length > 0 &&
                                      form.values.plan_benefits.length < 10 &&
                                      !tab.isPrefilled && (
                                        <button
                                          type="button"
                                          className="btn d-flex align-items-center"
                                          style={{
                                            backgroundColor: "transparent",
                                            color: "#0B4FE4",
                                          }}
                                          onClick={() => push("")}
                                        >
                                          <span className="me-2">+</span> Add
                                          Benefit
                                        </button>
                                      )}
                                  </>
                                )}
                              </FieldArray>
                            </div>
                          </div>
                          <div className="row">
                            <div className="col-md-12 mb-3">
                              {/* <span className="text-danger">*</spa> */}
                              <label htmlFor="add_ons" className="form-label">
                                Add-ons
                                {/* <CTooltip content="Add multiple add-ons with name and price">
                                  <span className="ms-2" style={{ marginLeft: "5px" }}>
                                    <i className="bi bi-info-circle"></i>
                                  </span>
                                </CTooltip> */}
                              </label>
                              <FieldArray name="add_ons">
                                {({ push, remove, form }) => (
                                  <>
                                    {form.values.add_ons?.length === 0 &&
                                      !tab.isPrefilled && (
                                        <div className="d-flex justify-content-start mb-2">
                                          <button
                                            type="button"
                                            className="btn d-flex align-items-center"
                                            style={{
                                              backgroundColor: "transparent",
                                              color: "#0B4FE4",
                                            }}
                                            onClick={() =>
                                              push({
                                                add_on_name: "",
                                                add_on_price: "",
                                              })
                                            }
                                          >
                                            <span className="me-2">+</span> Add
                                            Add-ons
                                          </button>
                                        </div>
                                      )}
                                    {form.values.add_ons?.map(
                                      (addOn, index) => (
                                        <div key={index} className="mb-2">
                                          <div className="d-flex">
                                            <div
                                              className="flex-grow-2 me-2 position-relative"
                                              style={{ flex: "2 1 0" }}
                                            >
                                              <input
                                                id={`add_ons.${index}.add_on_name`}
                                                name={`add_ons.${index}.add_on_name`}
                                                type="text"
                                                placeholder="Enter Name"
                                                maxLength={50}
                                                className={`form-control pe-5 ${
                                                  form.touched.add_ons?.[index]
                                                    ?.add_on_name &&
                                                  form.errors.add_ons?.[index]
                                                    ?.add_on_name
                                                    ? "is-invalid"
                                                    : ""
                                                }`}
                                                disabled={
                                                  formDisabled ||
                                                  tab.isPrefilled
                                                } // Disable for prefilled tabs
                                                onChange={(e) => {
                                                  setFieldValue(
                                                    `add_ons.${index}.add_on_name`,
                                                    e.target.value
                                                  );
                                                  updateTabValues(tab.key, {
                                                    add_ons: values.add_ons.map(
                                                      (a, i) =>
                                                        i === index
                                                          ? {
                                                              ...a,
                                                              add_on_name:
                                                                e.target.value,
                                                            }
                                                          : a
                                                    ),
                                                  });
                                                }}
                                                onBlur={() => {
                                                  handleSaveDraft(false);
                                                }}
                                                value={addOn.add_on_name || ""} // Ensure value is a string
                                              />
                                              <div
                                                className="position-absolute top-50 end-0 translate-middle-y"
                                                style={{
                                                  color: "#6c757d",
                                                  marginRight: "1em", // Default margin
                                                  ...(form.touched.add_ons?.[
                                                    index
                                                  ]?.add_on_name &&
                                                  form.errors.add_ons?.[index]
                                                    ?.add_on_name
                                                    ? { marginRight: "1.9rem" }
                                                    : {}),
                                                }}
                                              >
                                                {addOn.add_on_name?.length}/50
                                              </div>
                                            </div>
                                            <div
                                              className="flex-grow-1 position-relative"
                                              style={{ flex: "1 1 0" }}
                                            >
                                              <input
                                                id={`add_ons.${index}.add_on_price`}
                                                name={`add_ons.${index}.add_on_price`}
                                                type="text"
                                                placeholder="Enter Price"
                                                className={`form-control ps-5 ${
                                                  form.touched.add_ons?.[index]
                                                    ?.add_on_price &&
                                                  form.errors.add_ons?.[index]
                                                    ?.add_on_price
                                                    ? "is-invalid"
                                                    : ""
                                                }`}
                                                value={formatNumberWithCommas(
                                                  addOn.add_on_price
                                                )}
                                                disabled={
                                                  formDisabled ||
                                                  tab.isPrefilled
                                                } // Disable for prefilled tabs
                                                onChange={(e) => {
                                                  const formattedValue =
                                                    formatToTwoDecimals(
                                                      e.target.value
                                                    );
                                                  setFieldValue(
                                                    `add_ons.${index}.add_on_price`,
                                                    formattedValue
                                                  );
                                                  updateTabValues(tab.key, {
                                                    add_ons: values.add_ons.map(
                                                      (a, i) =>
                                                        i === index
                                                          ? {
                                                              ...a,
                                                              add_on_price:
                                                                formattedValue,
                                                            }
                                                          : a
                                                    ),
                                                  });
                                                }}
                                                onBlur={() => {
                                                  handleSaveDraft(false);
                                                }}
                                              />
                                              <span
                                                className="position-absolute top-50 start-0 translate-middle-y ms-2"
                                                style={{ color: "#6c757d" }}
                                              >
                                                RM
                                              </span>
                                            </div>
                                            {!tab.isPrefilled && (
                                              <CButton
                                                className="btn btn-sm btn-danger text-white ms-2"
                                                title="Delete"
                                                onClick={() => {
                                                  remove(index);
                                                }}
                                              >
                                                <i className=" bi bi-trash-fill"></i>
                                              </CButton>
                                            )}
                                          </div>
                                          {(form.touched.add_ons?.[index]
                                            ?.add_on_name &&
                                            form.errors.add_ons?.[index]
                                              ?.add_on_name) ||
                                          (form.touched.add_ons?.[index]
                                            ?.add_on_price &&
                                            form.errors.add_ons?.[index]
                                              ?.add_on_price) ? (
                                            <div className="invalid-feedback d-block mt-1">
                                              Both fields are required if add-on
                                              is added
                                            </div>
                                          ) : null}
                                        </div>
                                      )
                                    )}
                                    {form.values.add_ons?.length > 0 &&
                                      !tab.isPrefilled && (
                                        <button
                                          type="button"
                                          className="btn d-flex align-items-center"
                                          style={{
                                            backgroundColor: "transparent",
                                            color: "#0B4FE4",
                                          }}
                                          onClick={() =>
                                            push({
                                              add_on_name: "",
                                              add_on_price: "",
                                            })
                                          } // Push a new object with default values
                                        >
                                          <span className="me-2">+</span> Add
                                          More
                                        </button>
                                      )}
                                  </>
                                )}
                              </FieldArray>
                            </div>
                          </div>
                        </Form>
                      )}
                    </Formik>
                  </CTabPanel>
                ))}
              </CTabContent>
              <div className="d-flex justify-content-end mt-3">
                {!isLocked && (
                  <button
                    type="button"
                    className="btn me-3 ansa-button-secondary"
                    onClick={handleSaveDraft}
                    disabled={isSavingDraft}
                  >
                    {isSavingDraft ? "Saving..." : "Save as Draft"}
                  </button>
                )}
                {order.motor_insurance_quotations &&
                  tabs.find((t) => t.key === activeTab)?.values?.id &&
                  tabs.find((t) => t.key === activeTab)?.values?.id !== "" && (
                    <button
                      type="button"
                      className="btn me-3 ansa-button-secondary"
                      onClick={() => {
                        const currentTab = tabs.find(
                          (t) => t.key === activeTab
                        );
                        updateQuotation(currentTab?.values?.id);
                      }}
                    >
                      Update Quotation
                    </button>
                  )}

                <button
                  type="button"
                  className="btn me-3 ansa-button-secondary"
                  onClick={() => navigate("/requests")}
                >
                  Back
                </button>
                <button
                  type="button"
                  className="btn ansa-button-primary"
                  onClick={() => handleSubmit(formikRefs.current)}
                  disabled={isSubmittingForm}
                >
                  Upload All
                </button>
              </div>
            </CTabs>
          </div>
        </div>
      )}
    </>
  );
};

const DeleteQuotationModal = ({ id, index, setTabs }) => {
  const [visible, setVisible] = useState(false);

  return (
    <>
      <div
        className="btn btn-sm btn-danger text-white"
        style={{
          marginLeft: "8px",
          cursor: "pointer",
          transition: "background-color 0.2s",
        }}
        onClick={() => setVisible(!visible)}
        onMouseEnter={(e) =>
          (e.currentTarget.style.backgroundColor = "#e0e0e0")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.backgroundColor = "#dc3545")
        }
        onMouseDown={(e) => (e.currentTarget.style.backgroundColor = "#d0d0d0")}
        onMouseUp={(e) => (e.currentTarget.style.backgroundColor = "#e0e0e0")}
      >
        <i
          className="bi bi-trash-fill"
          style={{
            transition: "color 0.2s",
          }}
          onMouseEnter={(e) => (e.target.style.color = "#ff4d4d")}
          onMouseLeave={(e) => (e.target.style.color = "#ffffff")}
          onMouseDown={(e) => (e.target.style.color = "#cc0000")}
          onMouseUp={(e) => (e.target.style.color = "#ff4d4d")}
        />
      </div>
      <CModal
        alignment="center"
        visible={visible}
        onClose={() => setVisible(false)}
        aria-labelledby="LiveDemoExampleLabel"
      >
        <CModalHeader>
          <CModalTitle id="LiveDemoExampleLabel">Delete Quotation</CModalTitle>
        </CModalHeader>
        <CModalBody>Are you sure you want to delete this quotation?</CModalBody>
        <CModalFooter>
          <CButton color="secondary" onClick={() => setVisible(false)}>
            Close
          </CButton>
          <CButton
            color="danger"
            onClick={async () => {
              const response = await deleteQuoteQuotation({
                id: id,
              });

              if (response.success) {
                setVisible(false);
                setTabs((prevTabs) =>
                  prevTabs.filter((tab, tabIndex) => tabIndex !== index)
                );
              }
            }}
          >
            Delete
          </CButton>
        </CModalFooter>
      </CModal>
    </>
  );
};

const UpdateQuotation = ({ onClick, onCancel }) => {
  return (
    <>
      <div
        style={{
          backgroundColor: "#f0f0f0",
          borderRadius: "50%",
          padding: "5px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginLeft: "8px",
          cursor: "pointer",
          transition: "background-color 0.2s",
        }}
        onClick={onCancel}
        onMouseEnter={(e) =>
          (e.currentTarget.style.backgroundColor = "#e0e0e0")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.backgroundColor = "#f0f0f0")
        }
        onMouseDown={(e) => (e.currentTarget.style.backgroundColor = "#d0d0d0")}
        onMouseUp={(e) => (e.currentTarget.style.backgroundColor = "#e0e0e0")}
      >
        <CIcon
          icon={cilX}
          style={{
            color: "gray",
            transition: "color 0.2s",
          }}
          onMouseEnter={(e) => (e.target.style.color = "#232323")}
          onMouseLeave={(e) => (e.target.style.color = "gray")}
          onMouseDown={(e) => (e.target.style.color = "#232323")}
          onMouseUp={(e) => (e.target.style.color = "#ff4d4d")}
        />
      </div>
      <div
        style={{
          backgroundColor: "#f0f0f0",
          borderRadius: "50%",
          padding: "5px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginLeft: "8px",
          cursor: "pointer",
          transition: "background-color 0.2s",
        }}
        onClick={onClick}
        onMouseEnter={(e) =>
          (e.currentTarget.style.backgroundColor = "#e0e0e0")
        }
        onMouseLeave={(e) =>
          (e.currentTarget.style.backgroundColor = "#f0f0f0")
        }
        onMouseDown={(e) => (e.currentTarget.style.backgroundColor = "#d0d0d0")}
        onMouseUp={(e) => (e.currentTarget.style.backgroundColor = "#e0e0e0")}
      >
        <CIcon
          icon={cilSave}
          style={{
            color: "gray",
            transition: "color 0.2s",
          }}
          onMouseEnter={(e) => (e.target.style.color = "#232323")}
          onMouseLeave={(e) => (e.target.style.color = "gray")}
          onMouseDown={(e) => (e.target.style.color = "#232323")}
          onMouseUp={(e) => (e.target.style.color = "#ff4d4d")}
        />
      </div>
    </>
  );
};

export default UploadQuotation;
